/**
 * Stage Mapping tab — Story SCH-2.6 (supersedes SCH-2.2).
 *
 * Maps SubContractorHub project.stage strings (e.g., "Site Survey",
 * "Completed", "ON HOLD") to Joules pipeline stages via a flat 1:1 lookup.
 * No drag-precedence; SCH-2.2's milestone-field-key precedence design has
 * been retired (it never worked on Quality Solar's real data — see SCH-2.6
 * probe report).
 *
 * Flow:
 *   1. On mount, read cached vocabulary from /stage-vocabulary.
 *   2. If never discovered, admin clicks "Discover stages" to issue
 *      POST /discover-stage-values (~4-8s warm, ~30s cold on Fly).
 *   3. Discovered stages render as rows; admin assigns each a Joules
 *      Pipeline → Stage via cascading dropdowns.
 *   4. Mapped rows whose stage value is no longer in the current
 *      vocabulary surface in a separate section with a "no longer in
 *      recent projects" advisory.
 *   5. Default landing stage selector at the bottom catches projects with
 *      unmapped or null `project.stage`.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw, Trash2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/components/ui/use-toast';

import {
  useCreateStageMapping,
  useDeleteStageMapping,
  useDiscoverStageValues,
  useSetDefaultLandingStage,
  useStageMappings,
  useStageVocabulary,
  useUpdateStageMapping,
} from '@/hooks/integrations/useStageMappings';
import { useSubContractorHubIntegration } from '@/hooks/integrations/useSubContractorHubConnection';
import { useTenantPipelinesAndStages } from '@/hooks/integrations/useTenantPipelinesAndStages';
import type {
  PipelineStageCandidate,
  StageMappingRow as StageMappingRowType,
} from '@/types/integrations/stageMapping';

// Sticky last-chosen pipeline (kept from SCH-2.2 — UX pattern unchanged).
function loadLastPipelineId(tenantId: string | undefined): number | null {
  if (!tenantId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`sch-stage-mapping-pipeline:${tenantId}`);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function saveLastPipelineId(tenantId: string | undefined, pipelineId: number): void {
  if (!tenantId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      `sch-stage-mapping-pipeline:${tenantId}`,
      String(pipelineId),
    );
  } catch {
    // localStorage may be disabled (Safari private). Silent failure is fine.
  }
}

interface PipelineMeta {
  id: number;
  name: string;
}

function derivePipelines(stages: PipelineStageCandidate[]): PipelineMeta[] {
  const seen = new Set<number>();
  const out: PipelineMeta[] = [];
  for (const stage of stages) {
    if (!seen.has(stage.pipelineId)) {
      seen.add(stage.pipelineId);
      out.push({ id: stage.pipelineId, name: stage.pipelineName });
    }
  }
  return out;
}

interface StageMappingTabProps {
  /**
   * Reports mutation pending state to the parent panel so the panel can
   * block onClose while any mutation is in flight (AC10 carry-over).
   */
  onMutationPendingChange?: (pending: boolean) => void;
}

export function StageMappingTab({ onMutationPendingChange }: StageMappingTabProps) {
  const { toast } = useToast();
  const vocabularyQuery = useStageVocabulary();
  const stageMappingsQuery = useStageMappings();
  const stagesQuery = useTenantPipelinesAndStages();
  const integrationQuery = useSubContractorHubIntegration();

  const discoverMutation = useDiscoverStageValues();
  const createMutation = useCreateStageMapping();
  const updateMutation = useUpdateStageMapping();
  const deleteMutation = useDeleteStageMapping();
  const defaultStageMutation = useSetDefaultLandingStage();

  const isAnyMutationPending =
    discoverMutation.isPending ||
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    defaultStageMutation.isPending;

  useEffect(() => {
    onMutationPendingChange?.(isAnyMutationPending);
  }, [isAnyMutationPending, onMutationPendingChange]);

  // ─── Loading + error states ─────────────────────────────────────────────
  if (
    vocabularyQuery.isLoading ||
    stageMappingsQuery.isLoading ||
    stagesQuery.isLoading
  ) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  if (vocabularyQuery.isError) {
    const err = vocabularyQuery.error as Error & { code?: string };
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not load stage vocabulary</AlertTitle>
        <AlertDescription>
          {err.message || 'Try refreshing the panel.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (stageMappingsQuery.isError) {
    const err = stageMappingsQuery.error as Error & { code?: string };
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not load stage mappings</AlertTitle>
        <AlertDescription>{err.message || 'Try refreshing the panel.'}</AlertDescription>
      </Alert>
    );
  }

  const vocabulary = vocabularyQuery.data;
  const rules = stageMappingsQuery.data?.rows ?? [];
  const pipelineStages = stagesQuery.data ?? [];
  const integration = integrationQuery.data;
  const tenantId = integration?.tenantId;
  const defaultLandingStageId = integration?.defaultLandingStageId ?? null;
  const pipelines = derivePipelines(pipelineStages);

  const isVocabularyEmpty =
    !vocabulary || !vocabulary.discoveredAt || vocabulary.stages.length === 0;

  // ─── Discovery handler ──────────────────────────────────────────────────
  function handleDiscover() {
    discoverMutation.mutate(undefined, {
      onSuccess: (result) => {
        if (result.error) {
          const msg =
            result.error.code === 'auth_failed'
              ? 'Connection authentication failed. Rotate the API key on the Connection tab and try again.'
              : result.error.code === 'connection_test_failed'
                ? 'Could not reach SubContractorHub. Try again in a moment.'
                : result.error.message;
          toast({
            title: 'Could not discover stages',
            description: msg,
            variant: 'destructive',
          });
        } else if (result.data) {
          toast({
            title: `Discovered ${result.data.stages.length} stage values`,
            description: `${result.data.projectsSampled} projects sampled across ${result.data.pagesSampled} pages.`,
          });
        }
      },
      onError: () =>
        toast({
          title: 'Could not discover stages',
          description: 'Please try again.',
          variant: 'destructive',
        }),
    });
  }

  function handleSetStage(externalStageValue: string, joulesStageId: number) {
    const existing = rules.find((r) => r.externalStageValue === externalStageValue);
    if (existing) {
      updateMutation.mutate(
        { id: existing.id, body: { joulesStageId } },
        {
          onSuccess: (result) => {
            if (result.error) {
              toast({
                title: 'Could not update mapping',
                description: result.error.message,
                variant: 'destructive',
              });
            } else {
              toast({ title: `Updated: ${externalStageValue}` });
            }
          },
          onError: () =>
            toast({
              title: 'Could not update mapping',
              description: 'Please try again.',
              variant: 'destructive',
            }),
        },
      );
    } else {
      createMutation.mutate(
        { externalStageValue, joulesStageId },
        {
          onSuccess: (result) => {
            if (result.error) {
              toast({
                title: 'Could not save mapping',
                description: result.error.message,
                variant: 'destructive',
              });
            } else if (result.data) {
              toast({ title: `Mapped: ${externalStageValue}` });
              const pipelineId = result.data.joulesPipelineId;
              if (pipelineId !== null) saveLastPipelineId(tenantId, pipelineId);
            }
          },
          onError: () =>
            toast({
              title: 'Could not save mapping',
              description: 'Please try again.',
              variant: 'destructive',
            }),
        },
      );
    }
  }

  function handleDelete(rule: StageMappingRowType) {
    deleteMutation.mutate(rule.id, {
      onSuccess: (result) => {
        if (result.error) {
          toast({
            title: 'Could not remove mapping',
            description: result.error.message,
            variant: 'destructive',
          });
        } else {
          toast({ title: `Removed: ${rule.externalStageValue}` });
        }
      },
      onError: () =>
        toast({
          title: 'Could not remove mapping',
          description: 'Please try again.',
          variant: 'destructive',
        }),
    });
  }

  function handleDefaultStageChange(stageId: number | null) {
    defaultStageMutation.mutate(
      { joulesStageId: stageId },
      {
        onSuccess: (result) => {
          if (result.error) {
            toast({
              title: 'Could not save default landing stage',
              description: result.error.message,
              variant: 'destructive',
            });
          } else {
            toast({
              title:
                stageId === null
                  ? 'Default landing stage cleared'
                  : 'Default landing stage saved',
            });
          }
        },
        onError: () =>
          toast({
            title: 'Could not save default landing stage',
            description: 'Please try again.',
            variant: 'destructive',
          }),
      },
    );
  }

  // ─── Render: empty state (vocabulary never discovered) ──────────────────
  if (isVocabularyEmpty) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold">Stage Mapping</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Map each SubContractorHub project stage to a Joules pipeline stage so
            imported projects land in the right place. Discover stages first to
            see what values SubContractorHub uses for your projects.
          </p>
        </div>

        <Card>
          <CardContent className="py-10 flex flex-col items-center gap-3">
            <p className="text-sm text-muted-foreground">
              No stages discovered yet. Sampling SubContractorHub for the project
              stages your tenant uses takes a few seconds.
            </p>
            <Button
              onClick={handleDiscover}
              disabled={discoverMutation.isPending}
              size="sm"
            >
              {discoverMutation.isPending ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
              )}
              Discover stages
            </Button>
            {discoverMutation.isPending && (
              <p className="text-xs text-muted-foreground">
                Discovering SubContractorHub stages…
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Derived rows: vocabulary + dormant mappings ────────────────────────
  const vocabularyStages = vocabulary.stages;
  const ruleByStageValue = new Map(rules.map((r) => [r.externalStageValue, r]));
  const vocabularyStageSet = new Set(vocabularyStages);

  // Sort vocabulary alphabetically (case-insensitive) for predictable order.
  const vocabularyRows: Array<{
    stageValue: string;
    rule: StageMappingRowType | undefined;
  }> = vocabularyStages
    .slice()
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .map((stageValue) => ({
      stageValue,
      rule: ruleByStageValue.get(stageValue),
    }));

  // Mappings whose external_stage_value is NOT in current vocabulary
  // (AC13: surface as "no longer in recent projects").
  const dormantRules = rules
    .filter((r) => !vocabularyStageSet.has(r.externalStageValue))
    .sort((a, b) =>
      a.externalStageValue
        .toLowerCase()
        .localeCompare(b.externalStageValue.toLowerCase()),
    );

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">Stage Mapping</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Map each SubContractorHub project stage to a Joules pipeline stage.
            Imported projects land in the mapped stage. The default landing stage
            below catches anything unmapped.
          </p>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={handleDiscover}
            disabled={isAnyMutationPending}
          >
            {discoverMutation.isPending ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
            )}
            Re-discover stages
          </Button>
          {vocabulary.discoveredAt && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Last discovered: {new Date(vocabulary.discoveredAt).toLocaleString()}
              {' · '}
              {vocabulary.projectsSampled} projects sampled
            </p>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">Discovered SCH stages</CardTitle>
            <Badge variant="outline">{vocabularyStages.length}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {vocabularyRows.map(({ stageValue, rule }) => (
            <StageMappingRowEditor
              key={stageValue}
              externalStageValue={stageValue}
              rule={rule}
              pipelineStages={pipelineStages}
              pipelines={pipelines}
              initialPipelineId={loadLastPipelineId(tenantId)}
              isUpdating={
                (updateMutation.isPending &&
                  rule &&
                  updateMutation.variables?.id === rule.id) ||
                (createMutation.isPending &&
                  createMutation.variables?.externalStageValue === stageValue)
              }
              isDeleting={
                deleteMutation.isPending &&
                rule !== undefined &&
                deleteMutation.variables === rule.id
              }
              disabled={isAnyMutationPending}
              onSetStage={(stageId) => handleSetStage(stageValue, stageId)}
              onDelete={rule ? () => handleDelete(rule) : undefined}
            />
          ))}
        </CardContent>
      </Card>

      {dormantRules.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Mapped but no longer in recent projects
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              These mappings still apply if a project with these stages comes
              through. Re-discover to refresh what counts as "recent".
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {dormantRules.map((rule) => (
              <StageMappingRowEditor
                key={rule.id}
                externalStageValue={rule.externalStageValue}
                rule={rule}
                pipelineStages={pipelineStages}
                pipelines={pipelines}
                initialPipelineId={loadLastPipelineId(tenantId)}
                isUpdating={
                  updateMutation.isPending && updateMutation.variables?.id === rule.id
                }
                isDeleting={
                  deleteMutation.isPending && deleteMutation.variables === rule.id
                }
                disabled={isAnyMutationPending}
                onSetStage={(stageId) =>
                  handleSetStage(rule.externalStageValue, stageId)
                }
                onDelete={() => handleDelete(rule)}
                dormantBadge
              />
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Default landing stage</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-2">
            Used when a project's SubContractorHub stage isn't mapped above (or
            is empty). Leave unset to fall back to the primary pipeline's first
            stage at sync time.
          </p>
          <DefaultLandingStageSelector
            stages={pipelineStages}
            pipelines={pipelines}
            value={defaultLandingStageId}
            onChange={handleDefaultStageChange}
            disabled={defaultStageMutation.isPending}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ─── StageMappingRowEditor ────────────────────────────────────────────────
interface StageMappingRowEditorProps {
  externalStageValue: string;
  rule: StageMappingRowType | undefined; // undefined = not yet mapped
  pipelineStages: PipelineStageCandidate[];
  pipelines: PipelineMeta[];
  /** Sticky default pipeline pre-selection for unmapped rows. */
  initialPipelineId: number | null;
  isUpdating: boolean | undefined;
  isDeleting: boolean;
  disabled: boolean;
  onSetStage: (joulesStageId: number) => void;
  onDelete?: () => void;
  dormantBadge?: boolean;
}

function StageMappingRowEditor({
  externalStageValue,
  rule,
  pipelineStages,
  pipelines,
  initialPipelineId,
  isUpdating,
  isDeleting,
  disabled,
  onSetStage,
  onDelete,
  dormantBadge,
}: StageMappingRowEditorProps) {
  const isMapped = !!rule;

  // Pipeline shown for the cascading selectors. For mapped rows, default to
  // the rule's pipeline. For unmapped rows, default to sticky or first.
  const fallbackPipelineId =
    rule?.joulesPipelineId ??
    (initialPipelineId !== null &&
    pipelines.some((p) => p.id === initialPipelineId)
      ? initialPipelineId
      : (pipelines[0]?.id ?? null));

  const [pendingPipelineId, setPendingPipelineId] = useState<number | null>(
    fallbackPipelineId,
  );

  // Re-sync pipeline state when the rule's pipeline changes from the server
  // (e.g., after a successful update lands a new pipeline).
  useEffect(() => {
    if (rule?.joulesPipelineId !== undefined && rule?.joulesPipelineId !== null) {
      setPendingPipelineId(rule.joulesPipelineId);
    }
  }, [rule?.joulesPipelineId]);

  const pipelineOptions = useMemo(
    () => pipelines.map((p) => ({ value: String(p.id), label: p.name })),
    [pipelines],
  );

  const stageOptions = useMemo(
    () =>
      pipelineStages
        .filter((s) => s.pipelineId === pendingPipelineId)
        .map((s) => ({ value: String(s.id), label: s.stageName })),
    [pipelineStages, pendingPipelineId],
  );

  const stageDropdownValue =
    rule && pendingPipelineId === rule.joulesPipelineId
      ? String(rule.joulesStageId)
      : '';

  function handlePipelineChange(value: string) {
    const parsed = Number(value);
    setPendingPipelineId(Number.isFinite(parsed) ? parsed : null);
  }

  function handleStageChange(value: string) {
    if (value === '') return;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return;
    if (rule && parsed === rule.joulesStageId) return; // no-op
    onSetStage(parsed);
  }

  const isPending = !!isUpdating || isDeleting;

  return (
    <div className="flex items-center gap-2 rounded-md border p-2">
      <div className="min-w-0 flex-1 grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{externalStageValue}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            {!isMapped && (
              <Badge variant="outline" className="text-[10px] py-0">
                Not mapped
              </Badge>
            )}
            {dormantBadge && (
              <Badge
                variant="outline"
                className="text-[10px] py-0 text-amber-700 border-amber-300"
                title="Not seen in the most recent project sample"
              >
                Not in recent
              </Badge>
            )}
          </div>
        </div>
        <div className="min-w-0">
          <Label className="sr-only">Pipeline</Label>
          <SearchableSelect
            options={pipelineOptions}
            value={pendingPipelineId === null ? '' : String(pendingPipelineId)}
            onValueChange={handlePipelineChange}
            placeholder={rule?.joulesPipelineName ?? 'Choose a pipeline'}
            disabled={isPending || disabled}
            inDialog
          />
        </div>
        <div className="min-w-0">
          <Label className="sr-only">Stage</Label>
          <SearchableSelect
            options={stageOptions}
            value={stageDropdownValue}
            onValueChange={handleStageChange}
            placeholder={
              pendingPipelineId === null
                ? 'Pick a pipeline first'
                : (rule && pendingPipelineId === rule.joulesPipelineId
                    ? (rule.joulesStageName ?? '(stage no longer exists)')
                    : 'Choose a stage')
            }
            disabled={isPending || disabled || pendingPipelineId === null}
            inDialog
          />
        </div>
      </div>

      {onDelete && (
        <Button
          size="icon"
          variant="ghost"
          onClick={onDelete}
          disabled={isPending || disabled}
          aria-label={`Remove mapping for ${externalStageValue}`}
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>
      )}
    </div>
  );
}

// ─── DefaultLandingStageSelector (preserved from SCH-2.2) ─────────────────
interface DefaultLandingStageSelectorProps {
  stages: PipelineStageCandidate[];
  pipelines: PipelineMeta[];
  value: number | null;
  onChange: (stageId: number | null) => void;
  disabled?: boolean;
}

function DefaultLandingStageSelector({
  stages,
  pipelines,
  value,
  onChange,
  disabled,
}: DefaultLandingStageSelectorProps) {
  const savedStage = value !== null ? stages.find((s) => s.id === value) : undefined;
  const initialPipelineId = savedStage?.pipelineId ?? pipelines[0]?.id ?? null;

  const [pendingPipelineId, setPendingPipelineId] = useState<number | null>(
    initialPipelineId,
  );

  useEffect(() => {
    setPendingPipelineId(savedStage?.pipelineId ?? pipelines[0]?.id ?? null);
  }, [savedStage?.pipelineId, pipelines]);

  const pipelineOptions = pipelines.map((p) => ({
    value: String(p.id),
    label: p.name,
  }));

  const stageOptions = stages
    .filter((s) => s.pipelineId === pendingPipelineId)
    .map((s) => ({ value: String(s.id), label: s.stageName }));

  const stageDropdownValue =
    value !== null && savedStage?.pipelineId === pendingPipelineId
      ? String(value)
      : '';

  function handlePipelineChange(v: string) {
    const parsed = Number(v);
    setPendingPipelineId(Number.isFinite(parsed) ? parsed : null);
  }

  function handleStageChange(v: string) {
    if (v === '') return;
    const parsed = Number(v);
    if (!Number.isFinite(parsed)) return;
    onChange(parsed);
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <SearchableSelect
          options={pipelineOptions}
          value={pendingPipelineId === null ? '' : String(pendingPipelineId)}
          onValueChange={handlePipelineChange}
          placeholder="Choose a pipeline"
          disabled={disabled}
          inDialog
        />
        <SearchableSelect
          options={stageOptions}
          value={stageDropdownValue}
          onValueChange={handleStageChange}
          placeholder={
            pendingPipelineId === null
              ? 'Pick a pipeline first'
              : value !== null && savedStage?.pipelineId !== pendingPipelineId
                ? 'Pick a stage'
                : 'Choose a default stage'
          }
          disabled={disabled || pendingPipelineId === null}
          inDialog
        />
      </div>
      {value !== null && (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground underline disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => onChange(null)}
          disabled={disabled}
        >
          Clear default landing stage
        </button>
      )}
    </div>
  );
}
